import React from "react";

/**
 * The sidebar's marks, drawn on one 24x24 grid from the shapes each icon has
 * always used — Discovery's circle, square and triangle; Scan's dial and blip;
 * the bell for Events log. Three treatments are in play, picked per icon:
 *
 *   panels   filled shapes stepping down in opacity
 *   outline  every shape as a 1.8 stroke
 *   duotone  a tinted body carrying solid detail
 *
 * Strokes that enclose no area — a meridian, the radar sweep, the console's
 * prompt — stay strokes whatever the treatment, since a fill would leave a
 * sliver. Everything paints in currentColor, so the rail's selected-white and
 * hover-orange keep working untouched.
 *
 * Generated from the specimen sheet; edit the geometry here, not by hand in
 * the sidebar.
 */

/** Dashboard — four panels, drawn panels. */
export const NavDashboard = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x={3.6} y={3.6} width={7.8} height={9.8} rx={1.8} fill="currentColor" />
    <rect x={12.6} y={3.6} width={7.8} height={5.8} rx={1.8} fill="currentColor" opacity={0.8} />
    <rect x={12.6} y={11.6} width={7.8} height={9.8} rx={1.8} fill="currentColor" opacity={0.55} />
    <rect x={3.6} y={15.6} width={7.8} height={5.8} rx={1.8} fill="currentColor" opacity={0.4} />
  </svg>
);

/** Discovery — circle, square, triangle, drawn panels. */
export const NavDiscovery = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x={3.4} y={12.4} width={9.2} height={8} rx={1.6} fill="currentColor" />
    <circle cx={16.9} cy={6.6} r={3.9} fill="currentColor" opacity={0.8} />
    <path d="M7.9 2.9 L12.6 10.5 H3.2 Z" fill="currentColor" opacity={0.55} />
  </svg>
);

/** App — a window with a detached title bar and a prompt. Drawn in currentColor
    rather than the flat black it arrived as, so it takes the rail's white and
    the header's orange: the body at 0.3, the prompt at 0.55, the bar solid.

    Its artboard is 22x19 where every other mark here is square, so it is given
    a 24x24 box and left to scale into it — the viewBox keeps the proportions
    and centres the result, rather than stretching it to fill. */
export const NavApp = (
  <svg width="24" height="24" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path opacity={0.3} d="M22 5.91837C22 5.41117 21.4834 5 20.8462 5L2.5 5.00036H1.15385C0.516595 5.00036 0 5.41153 0 5.91873V18C0 18.5072 0.516595 18.9184 1.15385 18.9184L20.8462 18.918C21.4834 18.918 22 18.5068 22 17.9996V7.5V5.91837Z" fill="currentColor" />
    <path opacity={0.55} d="M11.5992 12.1949L5.59937 8.19531C5.09945 7.69537 3.59928 8.20098 3.59928 9.19509L8.09928 12.1951L3.09928 15.1949C2.59939 15.6951 4.09936 16.6951 5.09937 16.1951L11.5992 12.1949Z" fill="currentColor" />
    <path d="M22.0002 0.918368C22.0002 0.411167 21.4836 0 20.8464 0L2.50022 0.000360489H1.15406C0.516811 0.000360489 0.000216007 0.411528 0.000216007 0.918728L0 2.58199C0 3.08919 0.516591 3.50036 1.15384 3.50036L20.8462 3.5C21.4834 3.5 22 3.08883 22 2.58163L22.0002 2.5V0.918368Z" fill="currentColor" />
  </svg>
);

/** Designer — pen over a plate, drawn duotone. */
export const NavDesigner = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x={3.2} y={8.6} width={12.2} height={11.6} rx={1.8} fill="currentColor" opacity={0.3} />
    <path d="M18.6 3.2 L20.8 5.4 L12.6 13.6 L9.6 14.4 L10.4 11.4 Z" fill="currentColor" />
  </svg>
);

/** Deploy — upload, drawn duotone. */
export const NavDeploy = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 7.8 L18.4 14.2 H15 V20 H9 V14.2 H5.6 Z" fill="currentColor" opacity={0.3} />
    <rect x={6} y={3.2} width={12} height={2.6} rx={1.3} fill="currentColor" />
  </svg>
);

/** Events log — bell, drawn panels. */
export const NavEventLog = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 3 A6.6 6.6 0 0 1 18.6 9.6 V14.2 L20.4 17.4 H3.6 L5.4 14.2 V9.6 A6.6 6.6 0 0 1 12 3 Z" fill="currentColor" />
    <path d="M9.4 18.8 H14.6 A2.6 2.6 0 0 1 9.4 18.8 Z" fill="currentColor" opacity={0.8} />
  </svg>
);

/** Forge — layered stack, drawn duotone. */
export const NavForge = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 2.6 L21.4 7.2 L12 11.8 L2.6 7.2 Z" fill="currentColor" opacity={0.3} />
    <path d="M2.6 12 L12 16.6 L21.4 12" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.6 16.4 L12 21 L21.4 16.4" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Multiverse — linked nodes, drawn duotone. */
export const NavMultiverse = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx={6.8} cy={6.8} r={3.9} fill="currentColor" opacity={0.3} />
    <rect x={13.2} y={13.2} width={7.4} height={7.4} rx={1.8} fill="currentColor" />
    <path d="M9.7 9.7 L13.4 13.4" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Security Blast — particle field, drawn panels. */
export const NavSecurity = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx={12} cy={12} r={2.1} fill="currentColor" />
    <circle cx={5} cy={6.4} r={1.2} fill="currentColor" opacity={0.8} />
    <circle cx={5} cy={12} r={1.2} fill="currentColor" opacity={0.55} />
    <circle cx={5} cy={17.6} r={1.2} fill="currentColor" opacity={0.4} />
    <circle cx={10.2} cy={3.8} r={1.2} fill="currentColor" />
    <circle cx={10.2} cy={20.2} r={1.2} fill="currentColor" opacity={0.8} />
    <circle cx={15.4} cy={6.4} r={1.2} fill="currentColor" opacity={0.55} />
    <circle cx={15.4} cy={17.6} r={1.2} fill="currentColor" opacity={0.4} />
    <circle cx={19.4} cy={9.2} r={1.2} fill="currentColor" />
    <circle cx={19.4} cy={14.8} r={1.2} fill="currentColor" opacity={0.8} />
  </svg>
);

/** Monitor — globe, drawn duotone. */
export const NavMonitor = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx={12} cy={12} r={8.8} fill="currentColor" opacity={0.3} />
    <path d="M12 3.2 A 5.2 8.8 0 0 0 12 20.8" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 3.2 A 5.2 8.8 0 0 1 12 20.8" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.4 12 H20.6" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Scan — radar dial, drawn duotone. */
export const NavScan = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx={12} cy={12} r={8.8} fill="currentColor" opacity={0.3} />
    <circle cx={12} cy={12} r={4.4} fill="currentColor" />
    <path d="M12 12 L18.4 7.6" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
    <circle cx={17.3} cy={8.4} r={1.5} fill="currentColor" />
  </svg>
);

/** Reports — bars and points, drawn panels. */
export const NavReports = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x={3.6} y={14.4} width={3.4} height={6} rx={1.2} fill="currentColor" />
    <rect x={8.6} y={10.2} width={3.4} height={10.2} rx={1.2} fill="currentColor" opacity={0.8} />
    <rect x={13.6} y={14} width={3.4} height={6.4} rx={1.2} fill="currentColor" opacity={0.55} />
    <rect x={18.6} y={10.2} width={2.8} height={10.2} rx={1.2} fill="currentColor" opacity={0.4} />
    <circle cx={5.3} cy={11.4} r={1.3} fill="currentColor" />
    <circle cx={10.3} cy={7.2} r={1.3} fill="currentColor" opacity={0.8} />
    <circle cx={15.3} cy={11} r={1.3} fill="currentColor" opacity={0.55} />
    <circle cx={20} cy={7.2} r={1.3} fill="currentColor" opacity={0.4} />
  </svg>
);

/** Logout — hard disk, drawn panels. */
export const NavLogout = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x={3.2} y={11.8} width={17.6} height={7.8} rx={1.8} fill="currentColor" />
    <path d="M4.2 11.2 L7.8 4.6 H16.2 L19.8 11.2 Z" fill="currentColor" opacity={0.8} />
    <circle cx={16.8} cy={15.7} r={1.25} fill="currentColor" opacity={0.55} />
    <circle cx={12.9} cy={15.7} r={1.25} fill="currentColor" opacity={0.4} />
  </svg>
);
